import { useState, useCallback } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { crewApi } from '../services/api';
import type { SearchResult, FilterState, SearchMode } from '../types';

export function useSearch(initialQuery?: string) {
  const [searchQuery, setSearchQuery] = useState(initialQuery || '');
  const [searchMode, setSearchMode] = useState<SearchMode>('ai');
  const [filters, setFilters] = useState<FilterState>({
    rank: '',
    vesselCategory: '',
    nationality: '',
    minExperience: 0,
    minContracts: 0,
    availabilityDays: 30,
    vessel: '',
    status: '',
  });

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['crewSearch', searchQuery],
    queryFn: () => crewApi.search(searchQuery),
    enabled: !!searchQuery && searchQuery.trim().length > 0,
    staleTime: 60 * 1000, // 1 minute
  });

  const handleSearch = useCallback((query: string) => {
    if (!query.trim()) return;
    setSearchQuery(query);
  }, []);

  const handleFilterSearch = useCallback(() => {
    // Build query from filters
    const parts: string[] = [];
    
    if (filters.rank) {
      const rankLabels: Record<string, string> = {
        'MSTR': 'masters',
        'COFF': 'chief officers',
        'CENG': 'chief engineers',
        '3ENG': 'third engineers',
        '4ENG': 'fourth engineers',
      };
      parts.push(`Find ${rankLabels[filters.rank] || filters.rank}`);
    }
    
    if (filters.vesselCategory) {
      parts.push(`for ${filters.vesselCategory.toLowerCase()} vessels`);
    }
    
    if (filters.minExperience > 0) {
      parts.push(`with at least ${filters.minExperience} months experience`);
    }
    
    if (filters.minContracts > 0) {
      parts.push(`with ${filters.minContracts}+ contracts`);
    }
    
    if (filters.availabilityDays > 0) {
      parts.push(`available within ${filters.availabilityDays} days`);
    }

    const query = parts.join(' ') || 'Show all available crew';
    setSearchQuery(query);
  }, [filters]);

  const clearSearch = useCallback(() => {
    setSearchQuery('');
  }, []);

  return {
    searchQuery,
    setSearchQuery,
    searchMode,
    setSearchMode,
    filters,
    setFilters,
    handleSearch,
    handleFilterSearch,
    clearSearch,
    results: data,
    isLoading,
    error: error as Error | null,
    isSuccess: !!data,
  };
}

export function useVesselCategories() {
  return useQuery({
    queryKey: ['vesselCategories'],
    queryFn: crewApi.getVesselCategories,
    staleTime: Infinity,
  });
}

export function useActiveCrew(rank?: string) {
  return useQuery({
    queryKey: ['activeCrew', rank],
    queryFn: async () => {
      const result = await crewApi.getActiveCrew({ rank });
      return result.crew;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

export function useAvailableCrew(rank?: string) {
  return useQuery({
    queryKey: ['availableCrew', rank],
    queryFn: async () => {
      const result = await crewApi.getAvailableCrew({ rank });
      return result.crew;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

export function useReliefPlans(rank?: string) {
  return useQuery({
    queryKey: ['reliefPlans', rank],
    queryFn: async () => {
      const result = await crewApi.getReliefPlans({ rank });
      return result.plans;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

export function useSummary() {
  return useQuery({
    queryKey: ['summary'],
    queryFn: crewApi.getSummary,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}
